import { Injectable, WritableSignal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateTimeRangeValue } from '@core/dynamic-filters/interfaces/date-time-range-filter.interface';
import { GenericFormGroup } from '@core/interfaces/form.interface';
import { dateUtilsProxy } from '@core/utils/utils-proxy';
import { timeRegex } from '@shared/utils/regex.util';
import { Popover } from 'primeng/popover';

type SelectionMode = 'single' | 'range';

@Injectable()
export class DateTimeRangeService {
  normalizeValue(value: DateTimeRangeValue, selectionMode: SelectionMode, onlyToDate = false): DateTimeRangeValue {
    const { date, from: dateTimeRangeFrom, to: dateTimeRangeTo } = value;

    if (!date) {
      return;
    }

    const from = this.#formatTimeToString(dateTimeRangeFrom);
    const to = this.#formatTimeToString(dateTimeRangeTo);
    const rangeMode = selectionMode === 'range';
    let dateValue = date;

    if (rangeMode) {
      let [fromDate, toDate] = date as [Date, Date];

      if (!toDate) {
        toDate = fromDate;

        if (onlyToDate) {
          fromDate = null;
        }
      }

      dateValue = [fromDate, toDate];
    }

    const normalizedValue = {
      date: dateValue,
      from,
      to,
    };

    return normalizedValue;
  }

  toggleDatePicker(event: MouseEvent, popOver: Popover): void {
    event.stopPropagation();
    popOver.toggle(event);
  }

  onDateSelect(
    selectionMode: SelectionMode,
    date: [Date, Date],
    maxDateRange: number,
    maxDate: WritableSignal<Date>,
  ): void {
    if (selectionMode === 'range') {
      const [fromDate, toDate] = date || [null, null];

      // means only from date is selected
      // we need to set maxDate
      if (fromDate && !toDate && maxDateRange) {
        const maxDateRangeInDays = dateUtilsProxy.addDays(fromDate, maxDateRange);

        maxDate.set(maxDateRangeInDays);
      }

      if (fromDate && toDate) {
        maxDate.set(null);
      }
    }
  }

  resetTime(formGroup: FormGroup<GenericFormGroup<DateTimeRangeValue>>): void {
    formGroup.patchValue({
      from: '00:00',
      to: '00:00',
    });
  }

  checkFromToTime(name: string, formGroup: FormGroup<GenericFormGroup<DateTimeRangeValue>>): void {
    const fromToTime = formGroup.get(name)?.value;

    if (!fromToTime) {
      formGroup.get(name)?.setValue('00:00', { emitEvent: false });
    }
  }

  #formatTimeToString(time: Date | string): string {
    if (!time) {
      return '00:00';
    }

    if (typeof time === 'string' && timeRegex.test(time)) {
      return time;
    }

    return dateUtilsProxy.format(time instanceof Date ? time : new Date(time), 'HH:mm');
  }
}
